<script setup lang="ts">
import { errToStr, capitalize } from '@/helpers/tools';
import { Planungsgeschaeft } from '@/models/Planungsgeschaeft';
import { computed, onMounted, reactive, ref, type Ref } from 'vue';
import PlanungsgeschaeftEditStammdatenPanel from '@/components/PlanungsgeschaeftEditStammdatenPanel.vue';
import { useRouter } from 'vue-router';
import PlanungsgeschaeftMessageBoard from '@/components/PlanungsgeschaeftMessageBoard.vue';
import PlanungsgeschaeftMessageDlg from '@/components/PlanungsgeschaeftMessageDlg.vue';
import { ECH_STATUS } from '@/models/Status';
import ConfirmModal from '@/components/ConfirmModal.vue';
import { instance as getApi } from '@/helpers/api.js';
import { useAuthUser } from '@/helpers/globals';

const router = useRouter();
const user = useAuthUser();

const props = defineProps<{
    geschaeft: Planungsgeschaeft;
}>();

defineEmits<{
    'geschaeft-changed': [section: string | null];
}>();

const messageBoard: Ref<typeof PlanungsgeschaeftMessageBoard | null> = ref(null);

const state = reactive<{
    loading: boolean;
    showNewMessageDlg: boolean;
    showBaumeldungDlg: boolean;
    showDeleteConfirm: boolean;
    showWithdrawConfirm: boolean;
    backendError: string | null;
}>({
    loading: false,
    showNewMessageDlg: false,
    showBaumeldungDlg: false,
    showDeleteConfirm: false,
    showWithdrawConfirm: false,
    backendError: null
});

onMounted(async () => {
    state.backendError = null;
});

/*async function onNewGeschaeftClick() {
    state.loading = true;
    state.backendError = null;
    try {
        const newGeschaeft = await createNewGeschaeft();
        if (newGeschaeft?.id) {
            router.push({ name: 'planungsgeschaeft-edit-dashboard', params: { id: newGeschaeft.id } });
        }
    } catch (e) {
        console.error(e);
        state.backendError = errToStr(e);
    } finally {
        state.loading = false;
    }
}*/

// function showNewGemeindeDlg() {
//     state.showNewMessageDlg = true;
// }
// function showNewBauabschnittDlg() {
//     state.showBaumeldungDlg = true;
// }

function reloadMessageList() {
    messageBoard.value?.loadPpaMessages();
}

async function onDelete() {
    try {
        state.loading = true;
        state.showDeleteConfirm = false;
        state.backendError = null;
        await props.geschaeft.destroy();
        router.push({ name: 'planungsgeschaefte-my-geschaefte' });
    } catch (e) {
        console.error(e);
        state.backendError = errToStr(e);
    } finally {
        state.loading = false;
    }
}

async function onWithdraw() {
    try {
        state.loading = true;
        state.showWithdrawConfirm = false;
        state.backendError = null;
        await getApi().post('/Planungsgeschaeft/setStatus', {
            id: props.geschaeft.id,
            status: ECH_STATUS.WITHDRAWN
        });
        router.push({ name: 'planungsgeschaefte-my-geschaefte' });
    } catch (e) {
        console.error(e);
        state.backendError = errToStr(e);
    } finally {
        state.loading = false;
    }
}

async function onDuplicate() {
    state.backendError = null;
    try {
        const res = await getApi().post('/Planungsgeschaeft/duplizieren', {
            id: props.geschaeft.id
        });
        if (res && res.data && res.data.copy) {
            router.push({ name: 'planungsgeschaeft-edit-dashboard', params: { id: res.data.copy.id } });
        }
    } catch (e) {
        console.error(e);
        state.backendError = errToStr(e);
    } finally {
        state.loading = false;
    }
}

const showWithdrawn = computed(() => {
    return (
        props.geschaeft.aktueller_status_ebau_value === ECH_STATUS.SUBMITTED ||
        props.geschaeft.aktueller_status_ebau_value === ECH_STATUS.IN_PROGRESS ||
        props.geschaeft.aktueller_status_ebau_value === ECH_STATUS.SUSPENDED ||
        props.geschaeft.aktueller_status_ebau_value === ECH_STATUS.DEMAND_DOCUMENTS
    );
});
</script>

<template>
    <div class="ovr-grid">
        <div class="left">
            <h4 class="ktg-title-04 ktg-inner-box-padding mb-1_5rem">{{ $t('your_dashboard') }}</h4>

            <fieldset class="ktg-fieldset ktg-form-area-maxwidth py-0">
                <PlanungsgeschaeftMessageBoard
                    ref="messageBoard"
                    :geschaeft="props.geschaeft"
                ></PlanungsgeschaeftMessageBoard>
            </fieldset>
        </div>

        <div class="right">
            <h6 class="ktg-title-06 mb-1_5rem">{{ $t('stammdaten') }}</h6>
            <PlanungsgeschaeftEditStammdatenPanel
                class="ktg-box-margin"
                :geschaeft="props.geschaeft"
            ></PlanungsgeschaeftEditStammdatenPanel>
            <div class="mb-1rem">
                <h6 class="ktg-title-06 mt-2rem pb-0_5rem">{{ $t('aktionen') }}</h6>
            </div>
            <ktg-action-list class="ktg-box-margin">
                <RouterLink :to="{ name: 'planungsgeschaeft-edit-editor' }" custom v-slot="{ href, navigate }">
                    <ktg-action-item :href="href" :link-text="$t('starten')" @click.prevent="navigate()">
                        {{
                            props.geschaeft &&
                            user &&
                            user.isWriterOfGesuch(props.geschaeft) &&
                            [ECH_STATUS.STATUS_NEW, ECH_STATUS.DEMAND_DOCUMENTS].indexOf(
                                String(props.geschaeft.aktueller_status_ebau_value)
                            ) >= 0
                                ? $t('edit_planungsgeschaeft_further')
                                : $t('planungsgeschaeft_oeffnen')
                        }}
                    </ktg-action-item>
                </RouterLink>
                <ktg-action-item href="href" :link-text="$t('starten')" @click.prevent="onDuplicate">
                    {{ $t('duplicate_planungsgeschaeft') }}
                </ktg-action-item>
                <ktg-action-item
                    v-if="showWithdrawn"
                    href="href"
                    :link-text="$t('starten')"
                    @click.prevent="state.showWithdrawConfirm = true"
                >
                    {{ $t('withdraw_planungsgeschaeft') }}
                </ktg-action-item>
                <ktg-action-item
                    v-if="props?.geschaeft?.aktueller_status_ebau_value === ECH_STATUS.STATUS_NEW"
                    :link-text="capitalize($t('delete'))"
                    icon="trash-delete"
                    link-appearance="danger"
                    @click.prevent="state.showDeleteConfirm = true"
                >
                    {{ $t('delete_planungsgeschaeft') }}
                </ktg-action-item>
                <ktg-alert-banner
                    v-if="state.backendError"
                    appearance="danger"
                    dismissible
                    @dismiss="state.backendError = null"
                >
                    {{ state.backendError }}</ktg-alert-banner
                >
            </ktg-action-list>
        </div>

        <PlanungsgeschaeftMessageDlg
            v-model="state.showNewMessageDlg"
            :geschaeft="props.geschaeft"
            @created="reloadMessageList"
        ></PlanungsgeschaeftMessageDlg>
        <!-- <PlanungsgeschaeftBaumeldungDlg
            v-model="state.showBaumeldungDlg"
            :geschaeft="props.geschaeft"
            @created="reloadMessageList"
        ></PlanungsgeschaeftBaumeldungDlg> -->
        <ConfirmModal
            v-model="state.showDeleteConfirm"
            :title="$t('confirm_destroy_title')"
            :message="$t('confirm_dossier_destroy')"
            @cancel="state.showDeleteConfirm = false"
            @confirm="onDelete"
        ></ConfirmModal>
        <ConfirmModal
            v-model="state.showWithdrawConfirm"
            :title="$t('confirm_withdraw_title')"
            :message="$t('confirm_dossier_withdraw')"
            @cancel="state.showWithdrawConfirm = false"
            @confirm="onWithdraw"
        ></ConfirmModal>
    </div>
</template>

<style lang="scss" scoped>
.right {
    margin-top: 13px;
    height: fit-content;
    position: sticky;
    top: 8.5rem;
    width: 560px;
    flex-basis: 560px;
}
.left {
    flex-grow: 1 !important;
    margin-left: -1rem;
}
.ovr-grid {
    display: flex;
    gap: 5rem;
    margin-top: 5rem;
    margin-bottom: 5rem;
}
</style>
